import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import {
  LoaderFunction,
  useLoaderData,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import { ANIMATION_DURATION } from "@/constants/animations.constants";
import { MobileViewportContainer } from "@/modules/onboarding/components/MobileViewportContainer/MobileViewportContainer";
import {
  OnboardingBackButton,
  OnboardingBackButtonPlaceholder,
} from "@/modules/onboarding/components/OnboardingBackButton/OnboardingBackButton";
import { PlatformSearchModal } from "@/modules/onboarding/components/PlatformSearchModal/PlatformSearchModal";
import { LocalStorageService } from "@/modules/storage";
import { get } from "api";
import { LocationPickerScreen } from "modules/onboarding/screens/LocationPickerScreen";
import { PracticeLocation } from "modules/onboarding/utils/locationTransform";
import { queryClient } from "pages/Root";
import { currencyService } from "toolbox/CurrencyService";
import { waitObj } from "toolbox/Promise";
import { changeDayjsLocale } from "utils/dayjsUtils";

import { useAccountSetup } from "./utils/accountSetup";

const getLocations = async (platformId?: string | null) => {
  if (!platformId) throw new Error("Missing required platformId search param");
  const { locations } = await get(
    "/multi-platform/platform/{platformId}/locations",
    { platformId }
  );
  return locations as PracticeLocation[];
};

const load = async (request: Request) => {
  const searchParams = new URL(request.url).searchParams;
  const platformId = searchParams.get("platformId");
  const preselectedLocationId = searchParams.get("locationId");
  const referralCode = searchParams.get("referralCode");

  const platformImageUrl = await (async () => {
    const fromQuery = searchParams.get("platformImageUrl");
    if (fromQuery) return fromQuery;
    if (!platformId) return null;
    try {
      const theme = await get("/multi-platform/platform/{platformId}/theme", {
        platformId,
      });
      return theme?.logoImageUrl ?? null;
    } catch {
      return null;
    }
  })();

  const platform = queryClient
    .fetchQuery({
      queryKey: ["/platform/current"],
      queryFn: () => get("/platform/current"),
      staleTime: 0,
    })
    .catch(() => null)
    .then((p) => {
      if (p?.stripeRegion) {
        changeDayjsLocale(p.stripeRegion);
        currencyService.setCurrency(p.stripeRegion);
      }
      return p;
    });

  const referralCodeData = referralCode
    ? queryClient
        .fetchQuery({
          queryKey: ["/referral-codes/{referralCode}", referralCode],
          queryFn: () =>
            get("/referral-codes/{referralCode}", { referralCode }),
          staleTime: 0,
        })
        .catch(() => null)
    : Promise.resolve(null);

  // Only fetch locations if we have a platformId
  const locations = platformId ? getLocations(platformId) : Promise.resolve([]);

  return waitObj({
    platformId,
    platformImageUrl,
    preselectedLocationId,
    platform,
    locations,
    referralCodeData,
  });
};

type LoaderData = Awaited<ReturnType<typeof load>>;
export const v3LocationPickerLoader: LoaderFunction = ({ request }) =>
  load(request);

export default function V3LocationPicker() {
  const loaderData = useLoaderData() as LoaderData;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { handleAccountSetup } = useAccountSetup();

  // Use loaderData.platformId as the source of truth since navigation updates it
  const currentPlatformId = loaderData.platformId;

  const [showSearchModal, setShowSearchModal] = useState(
    !loaderData.platformId
  );

  // Track if user selected platform via search (should show back button and "find practice" link)
  // This is set to true when user selects from search modal
  const [cameFromSearch, setCameFromSearch] = useState(false);

  const referralCode = searchParams.get("referralCode");

  const { data: resolvedLocations = [], isLoading } = useQuery({
    queryKey: ["/multi-platform/platform", currentPlatformId, "locations"],
    queryFn: () => getLocations(currentPlatformId),
    initialData: loaderData.locations ?? [],
    enabled: !!currentPlatformId,
  });

  const getPreselectedLocationId = () => {
    if (loaderData.preselectedLocationId) {
      return loaderData.preselectedLocationId;
    }

    const referralLocationId =
      loaderData.referralCodeData?.platformLocation?.id.value;
    if (referralLocationId) {
      return referralLocationId;
    }

    return "";
  };

  const [selectedLocationId] = useState<string>(getPreselectedLocationId());

  const handlePlatformSelect = async (platformId: string) => {
    // Update the URL with the selected platformId
    const referralParam = referralCode ? `&referralCode=${referralCode}` : "";
    navigate(`/signin/location?platformId=${platformId}${referralParam}`, {
      replace: true,
    });
    setShowSearchModal(false);
    setCameFromSearch(true); // Mark that user came from search flow
  };

  const handleLocationSelect = async (locationId: string) => {
    if (!currentPlatformId) {
      return;
    }

    if (referralCode) {
      LocalStorageService.setItem("referralCode", referralCode);
    }

    await handleAccountSetup({
      platformId: currentPlatformId,
      locationId,
      navigate,
    });
  };

  const handleFindAnotherPractice = () => {
    setShowSearchModal(true);
  };

  useEffect(() => {
    if (resolvedLocations.length === 1) {
      handleLocationSelect(resolvedLocations[0].id);
    }
  }, [resolvedLocations]);

  if (resolvedLocations.length === 1) {
    return null;
  }

  return (
    <>
      {/* Platform Search Modal - overlays everything */}
      <PlatformSearchModal
        open={showSearchModal}
        onClose={() => setShowSearchModal(false)}
        onPlatformSelect={handlePlatformSelect}
        canClose={!!currentPlatformId} // Can only close if platform is already selected
      />

      {/* Main content */}
      <MobileViewportContainer
        header={
          cameFromSearch ? (
            <OnboardingBackButton prev="/signin/platform-picker" />
          ) : (
            <OnboardingBackButtonPlaceholder />
          )
        }
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: ANIMATION_DURATION, delay: 0.1 }}
        >
          <LocationPickerScreen
            locations={resolvedLocations}
            selectedLocation={selectedLocationId}
            onLocationSelect={handleLocationSelect}
            isLoading={isLoading && !!currentPlatformId}
            loadingMessage="Loading locations..."
            hideFindNearby={!cameFromSearch}
            onFindPracticeClick={handleFindAnotherPractice}
          />
        </motion.div>
      </MobileViewportContainer>
    </>
  );
}
